import { useEffect, useRef, useState } from "react";
import { Box } from "components";
import useIOSDetection from "hooks/useIOSDetection";
import useScanAnalytics from "hooks/useScanAnalytics";
import useTrackPageAnalytics from "hooks/useTrackPageAnalytics";
import { useLocation } from "react-router-dom";
import { useValidatePassportMutation } from "redux/queries/passport.query";
import { useGetSettingsQuery } from "redux/queries/settings.query";

import LanguageDrawer from "./flightList/LanguageDrawer";
import Body from "./scanPassport/Body";
import CircleStyled from "./scanPassport/CircleStyled";
import Header from "./scanPassport/Header";
import ImagePreviewModal from "./scanPassport/ImagePreviewModal";
import OCRScanner from "./scanPassport/OCRScanner";
import PreCameraDrawer from "./scanPassport/PreCameraDrawer";
import STEPS from "./scanPassport/Steps";
import ValidationDrawer from "./scanPassport/ValidationDrawer";

export default function ScanPassport() {
  const { sendAnalytics } = useTrackPageAnalytics();
  const { data: settingsData = {} } = useGetSettingsQuery(undefined, {
    refetchOnReconnect: true,
  });

  const { featureMap: featureFlags } = settingsData;
  const areAnalyticsEnabled = !featureFlags?.bypass_passport_scanning_analytics;
  const scanAnalytics = useScanAnalytics(areAnalyticsEnabled);
  const iosDetection = useIOSDetection();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const source = params.get("source"); // 'qr' or 'click'
  const isPreviewEnabled = params.get("preview") === "true"; // Enable image preview with ?preview=true

  useTrackPageAnalytics(
    source === "qr"
      ? {
          key: "navigate-smart-gate-from-qr",
          name: "Accessed Smart Gate from QR",
        }
      : {
          key: "navigate-smart-gate-click",
          name: "Accessed Smart Gate from Main Menu click",
        }
  );

  useEffect(() => {
    scanAnalytics.markFeatureEntry();
    // Set OS info in analytics
    let osString = "unknown";
    if (iosDetection.isMacOS) osString = "macos";
    if (iosDetection.isMobileSafari) osString = "ios_mobile_safari";
    else if (iosDetection.isSafari) osString = "safari";
    else if (iosDetection.isChrome) osString = "chrome";
    scanAnalytics.setOS(
      osString + (iosDetection.iOSVersion ? `_${iosDetection.iOSVersion}` : "")
    );
  }, [scanAnalytics, iosDetection]);

  const [currentStep, setCurrentStep] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validatePassport, { isLoading: isValidating }] =
    useValidatePassportMutation();
  const [scannerKey, setScannerKey] = useState(0);
  const [passportData, setPassportData] = useState(null);
  const [showPreCameraDrawer, setShowPreCameraDrawer] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [capturedImages, setCapturedImages] = useState(null);

  const pauseScanner = useRef(false);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      return;
    }
    if (areAnalyticsEnabled) {
      setShowPreCameraDrawer(true);
    } else {
      setShowScanner(true);
    }
  };

  const handleSkip = () => {
    if (areAnalyticsEnabled) {
      setShowPreCameraDrawer(true);
    } else {
      setShowScanner(true);
    }
  };

  const handleScanComplete = async (imageData) => {
    if (pauseScanner.current) {
      return;
    }
    pauseScanner.current = true;

    const { originalImage, processedImage } = imageData;

    // Log image info
    console.log(
      "📸 Original image:",
      (originalImage.size / 1024).toFixed(2),
      "KB"
    );
    console.log(
      "📸 Processed image:",
      (processedImage.size / 1024).toFixed(2),
      "KB (cropped & grayscale)"
    );

    // Store images for preview or validation
    setCapturedImages({ originalImage, processedImage });

    sendAnalytics({
      key: "smart-gate-image-captured",
      type: "event",
      name: "Successfully captured passport image",
    });

    if (isPreviewEnabled) {
      // Preview mode: Show modal for image inspection
      setShowImagePreview(true);
    } else {
      // Production mode: Go straight to validation
      await validatePassportImage(processedImage);
    }
  };

  const validatePassportImage = async (processedImage) => {
    // Mark image submission timestamp
    scanAnalytics.markImageSubmitted();

    // Show validation drawer with loading state
    setIsDrawerOpen(true);

    try {
      // Create FormData to send processed image to backend
      const formData = new FormData();
      formData.append("passport_image", processedImage, "passport.jpg");

      const response = await validatePassport(formData).unwrap();

      // Extract backend timestamps from response
      if (response?.timestamps) {
        scanAnalytics.setBackendTimestamps(response.timestamps);
      }

      // Backend should return passport data along with validation result
      if (response?.data?.passportData) {
        const passportData = response.data.passportData;
        setPassportData(passportData);

        // Set gender in analytics if available
        if (passportData.genderId) {
          scanAnalytics.setGender(passportData.genderId);
        }

        // Set date of birth in analytics if available
        if (passportData.dateOfBirth) {
          scanAnalytics.setDateOfBirth(passportData.dateOfBirth);
        }
      }

      // Mark flow end timestamp after result is ready to be shown
      scanAnalytics.markFlowEnd();

      setValidationResult(response);
    } catch (error) {
      // Mark flow end even on error
      scanAnalytics.markFlowEnd();

      setValidationResult({
        status: "error",
        message:
          error?.data?.message ||
          error?.message ||
          "Unable to validate passport. Please try again.",
      });
    }
  };

  const handleContinueFromPreview = async () => {
    // Close preview modal
    setShowImagePreview(false);

    if (!capturedImages) return;

    const { processedImage } = capturedImages;
    await validatePassportImage(processedImage);
  };

  const handleClose = () => {
    scanAnalytics.sendAnalyticsIfNeeded({
      reason: "user_exit",
      detail: "user_exited_feature",
    });
    sendAnalytics({
      key: "smart-gate-click-close",
      type: "event",
      name: "User clicked on 'X' button and was redirected to main page",
    });
    // TODO: Do a better handling for handling the analytics
    setTimeout(() => {
      window.location.href = "/";
    }, 500);
  };

  const handleDrawerClose = () => {
    setValidationResult(null);
    setIsDrawerOpen(false);
    setShowScanner(true);
    setScannerKey((prev) => prev + 1);
    pauseScanner.current = false;
  };

  const handlePreCameraContinue = () => {
    setShowPreCameraDrawer(false);
    setShowScanner(true);
  };

  const handlePreCameraCancel = () => {
    window.location.href = "/";
  };

  const handleImagePreviewClose = () => {
    setShowImagePreview(false);
    setCapturedImages(null);
  };

  if (showPreCameraDrawer) {
    if (!areAnalyticsEnabled) {
      setShowPreCameraDrawer(false);
      setShowScanner(true);
      return null;
    }
    return (
      <PreCameraDrawer
        isOpen={showPreCameraDrawer}
        onContinue={handlePreCameraContinue}
        onCancel={handlePreCameraCancel}
        message="Please get ready to scan your passport. Make sure your document is visible and well-lit."
      />
    );
  }

  if (showScanner) {
    return (
      <>
        <OCRScanner
          pauseScanner={pauseScanner}
          key={scannerKey}
          onScanComplete={handleScanComplete}
          onClose={handleClose}
          scanAnalytics={scanAnalytics}
        />
        <ValidationDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          validationResult={validationResult}
          isValidating={isValidating}
          onContinue={handleClose}
          scanAnalytics={scanAnalytics}
          nationality={passportData?.passportNationality}
          gender={passportData?.genderId}
          dateOfBirth={passportData?.dateOfBirth}
        />
        {showImagePreview && capturedImages && (
          <ImagePreviewModal
            originalImage={capturedImages.originalImage}
            processedImage={capturedImages.processedImage}
            onClose={handleImagePreviewClose}
            onContinue={handleContinueFromPreview}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Box
        style={{
          height: "100svh",
          backgroundColor: "#797FD6",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CircleStyled circlePosition={STEPS[currentStep].circlePosition} />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Header />
          <Body
            content={STEPS[currentStep]}
            currentStep={currentStep}
            numberOfSteps={STEPS.length}
            onNext={handleNext}
            onSkip={handleSkip}
          />
        </div>
        <LanguageDrawer />
      </Box>
    </>
  );
}
